/* Atlas v3 — The Global Desk: the foreign / ADR universe that owns the
   AI-and-semis supply chain but isn't in the main US universe. Three tabs:

     US-Listed   — US-tradeable ADRs / OTC lines (live Polygon prices; tap a
                   row to open the TickerSheet; thin OTC lines get a THIN mark).
     Home Market — home-exchange-only names (Tokyo/Taiwan/Korea/EU), delayed
                   Yahoo quotes shown in local currency + a USD approximation.
                   Display-only (these need a foreign broker to trade).
     Pre-IPO     — private / pre-IPO watchlist. No ticker, no price.

   Reached from the More sheet. Brings its own shell (TickerStrip + BottomNav)
   like the other v3 screens. Data: useAdrUniverse() (the catalog) +
   useLivePrices (US tab) + useForeignQuotes (Home tab, lazy). Mirrors the
   Leverage Desk's structure, tokens, and row idiom. */
import { useState, useMemo, useRef, useEffect } from 'react'
import { TickerCtx, Dropdown } from './lib'
import { StockLogo } from '@/v2/components/StockLogo'
import { TickerStrip, BottomNav, SearchPill, DeskSwitcher } from './shell'
import { StockDetailProvider, useStockDetail } from '@/v2/lib/StockDetailProvider'
import { useAdrUniverse } from '@/v2/hooks/useAdrUniverse'
import { useLivePrices } from '@/v2/hooks/useLivePrices'
import { useForeignQuotes } from '@/v2/hooks/useForeignQuotes'
import { HeroCandles } from './HeroCandles'

const Mono = { fontFamily: 'var(--studio-font-mono)' }
const Sans = { fontFamily: 'var(--studio-font-sans)' }
const Display = { fontFamily: 'var(--studio-font-display)', fontStyle: 'italic' }

const CCY_SYM = { JPY: '¥', TWD: 'NT$', KRW: '₩', HKD: 'HK$', CNY: '¥', INR: '₹', EUR: '€', NOK: 'kr ', CHF: 'Fr ', USD: '$' }

// US tab ranks by move over a chosen window. Historical mode exposes change1w
// (5 trading days) — surfaced under "5D" like the Leverage Desk.
const TFS = ['1D', '5D', '1M', '6M', '1Y']
const CHANGE_FIELD = { '1D': 'change1d', '5D': 'change1w', '1M': 'change1m', '6M': 'change6m', '1Y': 'change1y' }

function fmtPct(v) {
  if (v == null) return '—'
  const a = Math.abs(v)
  return `${v >= 0 ? '+' : '−'}${a >= 10 ? a.toFixed(0) : a.toFixed(1)}%`
}
function fmtMoney(v) {
  if (v == null) return '—'
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtLocal(v, ccy) {
  if (v == null) return '—'
  const sym = CCY_SYM[ccy] || ''
  const dec = v >= 500 ? 0 : 2
  const n = v.toLocaleString('en-US', { minimumFractionDigits: dec, maximumFractionDigits: dec })
  return sym ? `${sym}${n}` : `${n} ${ccy || ''}`.trim()
}
function fmtUsd(v) {
  if (v == null) return null
  return '~$' + v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}
function fmtAsOf(iso) {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  } catch { return null }
}

/* ── segmented sub-tab control ── */
function SubTabs({ tab, setTab, counts }) {
  const TABS = [
    { id: 'us', label: 'US-Listed', n: counts.us },
    { id: 'home', label: 'Home Market', n: counts.home },
    { id: 'preipo', label: 'Pre-IPO', n: counts.preipo },
  ]
  return (
    <div style={{ display: 'flex', gap: 0, margin: '16px 20px 0', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius)', overflow: 'hidden' }}>
      {TABS.map((t, i) => {
        const on = tab === t.id
        return (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            flex: 1, padding: '9px 6px', cursor: 'pointer', border: 'none',
            borderLeft: i === 0 ? 'none' : '1px solid hsl(var(--studio-border))',
            background: on ? 'hsl(var(--studio-accent) / 0.12)' : 'transparent',
            ...Mono, fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase',
            color: on ? 'hsl(var(--studio-accent))' : 'hsl(var(--studio-text-subtle))',
            transition: 'background 140ms, color 140ms',
          }}>
            {t.label} <span style={{ opacity: 0.6 }}>{t.n}</span>
          </button>
        )
      })}
    </div>
  )
}

/* ── US-Listed row — live price + 1D, tap to open TickerSheet ── */
const US_GRID = { display: 'grid', gridTemplateColumns: '26px minmax(64px,1fr) 72px 56px', alignItems: 'center', gap: 10 }
function UsRow({ row, live, move, onOpen }) {
  const [hot, setHot] = useState(false)
  const ch = move
  const up = ch != null && ch >= 0
  const col = ch == null ? '--studio-text-subtle' : up ? '--studio-success' : '--studio-danger'
  return (
    <button onClick={() => onOpen(row.ticker)}
      onPointerDown={() => setHot(true)} onPointerUp={() => setHot(false)} onPointerCancel={() => setHot(false)} onPointerLeave={() => setHot(false)}
      style={{
        ...US_GRID, width: '100%', padding: '13px 20px', cursor: 'pointer', textAlign: 'left', border: 'none',
        borderTop: '1px solid hsl(var(--studio-border) / 0.4)',
        background: hot ? 'hsl(var(--studio-surface-2) / 0.55)' : 'transparent', transition: 'background 140ms',
      }}>
      <StockLogo ticker={row.ticker} size={26} rounded="50%" />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ ...Mono, fontSize: 13, color: 'hsl(var(--studio-text))' }}>{row.ticker}</span>
          {row.tradability === 'OTC_THIN' && (
            <span style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.5)', borderRadius: 3, padding: '1px 3px' }}>THIN</span>
          )}
        </div>
        <div style={{ ...Sans, fontSize: 10, color: 'hsl(var(--studio-text-subtle))', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.company}</div>
      </div>
      <span style={{ ...Mono, fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--studio-text-muted))', textAlign: 'right' }}>{fmtMoney(live?.price)}</span>
      <span style={{ ...Mono, fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: `hsl(var(${col}))`, textAlign: 'right' }}>{fmtPct(ch)}</span>
    </button>
  )
}

/* ── Home Market row — local price + USD approx, display only ── */
const HOME_GRID = { display: 'grid', gridTemplateColumns: '26px minmax(64px,1fr) auto', alignItems: 'center', gap: 10 }
function HomeRow({ row, quote }) {
  const local = quote?.local ?? row.last_price_local
  const ccy = quote?.ccy ?? row.last_ccy
  const usd = quote?.usd ?? row.last_price_usd
  const stale = quote?.stale
  const asOf = stale ? fmtAsOf(quote?.fetched_at ?? row.price_fetched_at) : null
  return (
    <div style={{ ...HOME_GRID, width: '100%', padding: '13px 20px', borderTop: '1px solid hsl(var(--studio-border) / 0.4)' }}>
      <StockLogo ticker={row.ticker} size={26} rounded="50%" />
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ ...Sans, fontSize: 12.5, color: 'hsl(var(--studio-text))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.company}</span>
          {row.tradability === 'NOT_ACCESSIBLE' && (
            <span style={{ ...Mono, fontSize: 7.5, letterSpacing: '0.1em', flexShrink: 0, color: 'hsl(var(--studio-accent))', border: '1px solid hsl(var(--studio-accent) / 0.5)', borderRadius: 3, padding: '1px 3px' }}>NO ACCESS</span>
          )}
        </div>
        <div style={{ ...Mono, fontSize: 9, letterSpacing: '0.04em', color: 'hsl(var(--studio-text-subtle))', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {row.local_ticker} · {(row.exchange || '').replace(/\s*\(.*\)/, '')}
        </div>
      </div>
      <div style={{ textAlign: 'right', minWidth: 0 }}>
        <div style={{ ...Mono, fontSize: 12.5, fontVariantNumeric: 'tabular-nums', color: 'hsl(var(--studio-text))' }}>{fmtLocal(local, ccy)}</div>
        <div style={{ ...Mono, fontSize: 9.5, color: 'hsl(var(--studio-text-subtle))', marginTop: 2 }}>
          {fmtUsd(usd) || '—'}{asOf ? ` · as of ${asOf}` : ''}
        </div>
      </div>
    </div>
  )
}

/* ── Pre-IPO row — static reference, no price ── */
function PreIpoRow({ row }) {
  return (
    <div style={{ padding: '14px 20px', borderTop: '1px solid hsl(var(--studio-border) / 0.4)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
        <span style={{ ...Sans, fontSize: 13.5, color: 'hsl(var(--studio-text))' }}>{row.company}</span>
        {row.valuation && <span style={{ ...Mono, fontSize: 11, color: 'hsl(var(--studio-accent-soft))', flexShrink: 0 }}>{row.valuation}</span>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5 }}>
        {row.status && <span style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'hsl(var(--studio-text-subtle))', border: '1px solid hsl(var(--studio-border))', borderRadius: 3, padding: '1px 5px' }}>{row.status}</span>}
        {row.expected_listing && <span style={{ ...Mono, fontSize: 9, color: 'hsl(var(--studio-text-subtle))' }}>{row.expected_listing}</span>}
      </div>
      {row.notes && <div style={{ ...Sans, fontSize: 11, lineHeight: 1.5, color: 'hsl(var(--studio-text-muted))', marginTop: 7 }}>{row.notes}</div>}
    </div>
  )
}

/* ── Leaders & Laggards band cell — top gainer / top loser hero (US tab only),
   with a real candlestick chart (candles by default). Tap opens the TickerSheet. ── */
function BandCell({ entry, up, tf, onOpen }) {
  const { row, move } = entry
  const c = up ? '--studio-success' : '--studio-danger'
  return (
    <button onClick={() => onOpen(row.ticker)}
      style={{ flex: 1, minWidth: 0, padding: '13px 14px', cursor: 'pointer', textAlign: 'left', background: 'transparent',
        border: 'none', borderLeft: up ? 'none' : '1px solid hsl(var(--studio-border))' }}>
      <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: `hsl(var(${c}))`, display: 'flex', alignItems: 'center', gap: 5 }}>
        {up ? '▲' : '▼'} {up ? 'Top gainer' : 'Top loser'} · {tf}
      </div>
      <div style={{ ...Display, fontSize: 27, lineHeight: 1, color: `hsl(var(${c}))`, marginTop: 7 }}>{fmtPct(move)}</div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, marginTop: 5, minWidth: 0 }}>
        <span style={{ ...Mono, fontSize: 12, color: 'hsl(var(--studio-text))' }}>{row.ticker}</span>
        <span style={{ ...Sans, fontSize: 10, color: 'hsl(var(--studio-text-subtle))', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.company}</span>
      </div>
      <div style={{ marginTop: 6 }}><HeroCandles ticker={row.ticker} range={tf} w={150} h={34} line /></div>
    </button>
  )
}

function GlobalDeskBody({ adr }) {
  const { openTicker } = useStockDetail()
  const { us, home, preipo, sectors, counts, loading } = adr
  const [tab, setTab] = useState('us')
  const [sector, setSector] = useState('all')
  const [tf, setTf] = useState('1D')       // US-tab timeframe
  const [sort, setSort] = useState('gain')  // 'gain' = biggest first (desc) | 'loss' = biggest drop first (asc)

  // Live US prices in historical mode (price + change1d/1w/1m/6m/1y), so the US
  // tab can rank by any timeframe. Foreign quotes lazily, only once the Home tab
  // is opened (avoids the 77-symbol Yahoo batch otherwise).
  const usTickers = useMemo(() => us.map((s) => s.ticker), [us])
  const { prices, loading: pricesLoading } = useLivePrices(tab === 'us' ? usTickers : [], 60_000, 'historical')
  const { quotes: foreign, loading: foreignLoading } = useForeignQuotes(tab === 'home')

  const field = CHANGE_FIELD[tf]

  // distinct Home sectors (catalog order) for that tab's filter
  const homeSectors = useMemo(() => {
    const seen = new Set(), out = []
    for (const r of home) if (r.sector && !seen.has(r.sector)) { seen.add(r.sector); out.push({ id: r.sector, label: r.sector }) }
    return out
  }, [home])

  const activeSectors = tab === 'us' ? sectors : tab === 'home' ? homeSectors : []

  // US rows → attach the selected-timeframe move → sort desc/asc (nulls last).
  // While the initial price fill is in flight, keep catalog order — ranking
  // half-filled moves reshuffles the list on every landing batch (the "glitchy
  // load"). One rank when the fill settles; revisits seed from the price cache
  // so they rank instantly.
  const usRows = useMemo(() => {
    const list = us
      .filter((r) => sector === 'all' || r.sector === sector)
      .map((r) => ({ row: r, move: (prices[r.ticker] || {})[field] }))
    if (!pricesLoading) list.sort((a, b) => {
      if (a.move == null && b.move == null) return a.row.ticker.localeCompare(b.row.ticker)
      if (a.move == null) return 1
      if (b.move == null) return -1
      return sort === 'loss' ? a.move - b.move : b.move - a.move
    })
    return list
  }, [us, sector, prices, field, sort, pricesLoading])
  const homeRows = useMemo(() => home.filter((r) => sector === 'all' || r.sector === sector), [home, sector])

  // Leaders & Laggards (US tab) = highest + lowest move, independent of sort
  // dir. Held until the fill settles — a mid-fill band flips tickers on every
  // batch and remounts its HeroCandles chart fetches.
  let lead = null, lag = null
  if (tab === 'us' && !pricesLoading) {
    for (const r of usRows) {
      if (r.move == null) continue
      if (!lead || r.move > lead.move) lead = r
      if (!lag || r.move < lag.move) lag = r
    }
    if (lead && lead === lag) lag = null
  }

  // US summary — average live move across rows that have a quote for the
  // window. '—' until the fill settles (a partial average ticks distractingly).
  const usAvg = useMemo(() => {
    if (pricesLoading) return null
    const vals = usRows.map((r) => r.move).filter((v) => v != null)
    if (!vals.length) return null
    return vals.reduce((a, b) => a + b, 0) / vals.length
  }, [usRows, pricesLoading])
  const usAvgUp = usAvg != null && usAvg >= 0

  // Count of US-tab names buyable on any US broker (incl. Robinhood) — a
  // major-exchange ADR/common listing, not an OTC line. Surfaced as a header
  // stat rather than a per-row badge (the OTC majority isn't "broken").
  const majorExch = useMemo(() => us.filter((r) => r.tradability === 'US_RETAIL_OK').length, [us])

  return (
    <TickerCtx.Provider value={openTicker}>
      <div style={{ position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
        background: 'radial-gradient(140% 75% at 50% -10%, hsl(var(--studio-background)) 0%, hsl(var(--studio-overlay)) 100%)' }}>
        {/* header — the DeskSwitcher is the eyebrow (UNIVERSE / LEVERAGE / GLOBAL) */}
        <div style={{ flexShrink: 0, padding: '20px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ minWidth: 0 }}><DeskSwitcher /></div>
            <SearchPill />
          </div>
          <div style={{ ...Display, fontSize: 33, color: 'hsl(var(--studio-text))', lineHeight: 1.04, marginTop: 6 }}>The Global Desk</div>
          <SubTabs tab={tab} setTab={(t) => { setTab(t); setSector('all') }} counts={counts} />

          {/* leaders & laggards — top gainer + top loser (US tab, selected window) */}
          {tab === 'us' && (lead || lag) && (
            <div style={{ display: 'flex', marginTop: 16, background: 'hsl(var(--studio-surface))', border: '1px solid hsl(var(--studio-border))', borderRadius: 'var(--studio-radius-lg)', overflow: 'hidden' }}>
              {lead && <BandCell entry={lead} up tf={tf} onOpen={openTicker} />}
              {lag && <BandCell entry={lag} up={false} tf={tf} onOpen={openTicker} />}
            </div>
          )}

          {/* summary stat line — adapts per tab */}
          <div style={{ ...Mono, fontSize: 9.5, letterSpacing: '0.1em', color: 'hsl(var(--studio-text-subtle))', marginTop: 12 }}>
            {tab === 'us' ? (
              <>{counts.us} ADRS · {majorExch} MAJOR-EXCHANGE · AVG {tf} <span style={{ color: `hsl(var(${usAvgUp ? '--studio-success' : '--studio-danger'}))` }}>{usAvg == null ? '—' : fmtPct(usAvg)}</span></>
            ) : tab === 'home' ? (
              <>{counts.home} HOME-MARKET NAMES</>
            ) : (
              <>{counts.preipo} PRE-IPO NAMES</>
            )}
          </div>
        </div>

        {/* column header — sector filter (us + home); the US tab adds a timeframe
            dropdown + sort toggle on the right, other tabs keep a static hint. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px 7px' }}>
          {activeSectors.length > 0 ? (
            <Dropdown value={sector} onChange={setSector} align="left" size={8.5} fallbackLabel="All sectors" minWidth={170} maxHeight={320}
              options={[{ value: 'all', label: 'All sectors' }, ...activeSectors.map((s) => ({ value: s.id, label: s.label }))]} />
          ) : <span />}
          {tab === 'us' ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <Dropdown value={tf} onChange={setTf} align="right" size={8.5} options={TFS.map((k) => ({ value: k, label: k }))} />
              <button onClick={() => setSort((s) => (s === 'gain' ? 'loss' : 'gain'))} aria-label="Toggle sort direction"
                style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'hsl(var(--studio-accent))', fontSize: 12, lineHeight: 1 }}>
                {sort === 'loss' ? '↑' : '↓'}
              </button>
            </span>
          ) : (
            <span style={{ ...Mono, fontSize: 8, letterSpacing: '0.12em', color: 'hsl(var(--studio-text-subtle))', textTransform: 'uppercase' }}>
              {tab === 'home' ? 'Local · USD' : 'Pipeline'}
            </span>
          )}
        </div>

        {/* rows */}
        <div className="atlas-scroll" style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {loading ? (
            <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '40px 20px', textAlign: 'center' }}>LOADING DESK…</div>
          ) : tab === 'us' ? (
            usRows.length === 0 ? (
              <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '40px 20px', textAlign: 'center' }}>NO NAMES</div>
            ) : (
              usRows.map((r) => <UsRow key={r.row.ticker} row={r.row} live={prices[r.row.ticker]} move={r.move} onOpen={openTicker} />)
            )
          ) : tab === 'home' ? (
            <>
              {foreignLoading && Object.keys(foreign).length === 0 && (
                <div style={{ ...Mono, fontSize: 10, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '14px 20px' }}>FETCHING DELAYED QUOTES…</div>
              )}
              {homeRows.length === 0 ? (
                <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '40px 20px', textAlign: 'center' }}>NO NAMES</div>
              ) : (
                <>
                  {homeRows.map((r) => <HomeRow key={r.ticker} row={r} quote={foreign[r.ticker]} />)}
                  <div style={{ ...Mono, fontSize: 8.5, letterSpacing: '0.06em', color: 'hsl(var(--studio-text-subtle))', padding: '16px 20px 4px', lineHeight: 1.6 }}>
                    Home-market listings — delayed quotes in local currency, USD approximate. Trading these needs a foreign brokerage.
                  </div>
                </>
              )}
            </>
          ) : preipo.length === 0 ? (
            <div style={{ ...Mono, fontSize: 11, letterSpacing: '0.08em', color: 'hsl(var(--studio-text-subtle))', padding: '40px 20px', textAlign: 'center' }}>NO NAMES</div>
          ) : (
            preipo.map((r) => <PreIpoRow key={r.ticker} row={r} />)
          )}
          <div style={{ height: 14 }} />
        </div>

        <div style={{ flexShrink: 0, borderTop: '1px solid hsl(var(--studio-border) / 0.5)', background: 'hsl(var(--studio-background) / 0.72)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
          <TickerStrip />
        </div>
        <BottomNav />
      </div>
    </TickerCtx.Provider>
  )
}

/* Fetch the catalog once here so the US rows double as the TickerSheet
   `extraStocks` — without it openTicker() no-ops for ADRs not in the main
   universe (StockDetailProvider guards unknown tickers). */
function GlobalDeskData() {
  const adr = useAdrUniverse()
  const extraStocks = useMemo(
    () => adr.us.map((s) => ({ ticker: s.ticker, name: s.company, sector: s.sector, tier: 'adr' })),
    [adr.us],
  )
  return (
    <StockDetailProvider extraStocks={extraStocks}>
      <GlobalDeskBody adr={adr} />
    </StockDetailProvider>
  )
}

export function GlobalDesk() {
  return (
    <div className="v3-root" style={{ height: 'var(--v3-vh, 100dvh)', display: 'flex', flexDirection: 'column' }}>
      <GlobalDeskData />
    </div>
  )
}
